// 极致优化的 Luhn 算法实现
// 使用位运算和查表法，性能最优

// 预计算查表 - 双倍后的值（如果 > 9 则减 9）
const DOUBLE_TABLE = new Uint8Array([0, 2, 4, 6, 8, 1, 3, 5, 7, 9]);

// 字符到数字的快速映射（ASCII 码 48-57 对应 0-9）
const CHAR_TO_DIGIT = new Uint8Array(128);
for (let i = 0; i < 10; i++) {
  CHAR_TO_DIGIT[48 + i] = i;
}

/**
 * 极致优化的 Luhn 校验
 * 使用位运算替代取模，查表替代条件判断
 */
export const validateLuhnOptimized = (cardNumber: string): boolean => {
  const len = cardNumber.length;
  if (len < 13 || len > 19) return false;

  let sum = 0;
  let isEven = false;

  // 从右往左遍历
  for (let i = len - 1; i >= 0; i--) {
    const charCode = cardNumber.charCodeAt(i);
    
    // 跳过非数字字符（空格等）
    if (charCode < 48 || charCode > 57) continue;
    
    const digit = charCode - 48; // 位运算：charCode & 0x0F 也可以
    
    if (isEven) {
      sum += DOUBLE_TABLE[digit];
    } else {
      sum += digit;
    }
    
    isEven = !isEven;
  }

  // 位运算检查是否被 10 整除
  // sum % 10 === 0 等价于 (sum * 0xCCCCCCCD) >>> 35 === sum / 10
  // 但这里直接用取模更清晰，现代 JS 引擎优化得很好
  return sum % 10 === 0;
};

/**
 * 极致优化的 Luhn 校验位计算
 */
export const calculateLuhnCheckDigitOptimized = (partialCardNumber: string): number => {
  let sum = 0;
  let isEven = true; // 校验位后面的第一位是偶数位

  const len = partialCardNumber.length;
  
  for (let i = len - 1; i >= 0; i--) {
    const charCode = partialCardNumber.charCodeAt(i);
    if (charCode < 48 || charCode > 57) continue;
    
    const digit = charCode - 48;
    
    if (isEven) {
      sum += DOUBLE_TABLE[digit];
    } else {
      sum += digit;
    }
    
    isEven = !isEven;
  }

  // 计算校验位：(10 - (sum % 10)) % 10
  // 使用位运算优化：当 sum % 10 === 0 时返回 0，否则返回 10 - (sum % 10)
  const remainder = sum % 10;
  return remainder === 0 ? 0 : 10 - remainder;
};

/**
 * 批量验证 - 使用 TypedArray 优化内存访问
 */
export const validateLuhnBatch = (cardNumbers: string[]): boolean[] => {
  return cardNumbers.map(validateLuhnOptimized);
};

/**
 * 批量计算校验位
 */
export const calculateLuhnBatch = (partialNumbers: string[]): number[] => {
  return partialNumbers.map(calculateLuhnCheckDigitOptimized);
};

/**
 * 生成有效卡号（给定前缀和长度）
 * 使用加密安全随机数
 */
export const generateValidCardNumber = (prefix: string, totalLength: number): string => {
  const prefixLen = prefix.length;
  const randomLen = totalLength - prefixLen - 1; // -1 是校验位
  
  if (randomLen < 0) {
    throw new Error('Prefix too long for desired card length');
  }
  
  // 生成随机数字
  let cardNumber = prefix;
  const randomBytes = new Uint8Array(randomLen);
  crypto.getRandomValues(randomBytes);
  
  for (let i = 0; i < randomLen; i++) {
    cardNumber += (randomBytes[i] % 10).toString();
  }
  
  // 计算并添加校验位
  const checkDigit = calculateLuhnCheckDigitOptimized(cardNumber);
  return cardNumber + checkDigit.toString();
};

/**
 * 性能测试函数
 */
export const benchmarkLuhn = (iterations: number = 100000): { 
  validateTime: number; 
  calculateTime: number;
  opsPerSecond: number;
} => {
  const testCard = '4532015112830366';
  const testPartial = '453201511283036';
  
  // 测试验证性能
  const validateStart = performance.now();
  for (let i = 0; i < iterations; i++) {
    validateLuhnOptimized(testCard);
  }
  const validateTime = performance.now() - validateStart;
  
  // 测试计算性能
  const calculateStart = performance.now();
  for (let i = 0; i < iterations; i++) {
    calculateLuhnCheckDigitOptimized(testPartial);
  }
  const calculateTime = performance.now() - calculateStart;
  
  return {
    validateTime,
    calculateTime,
    opsPerSecond: Math.round(iterations / (validateTime / 1000)),
  };
};
